import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Award, Users, TrendingUp, Crown, Star, Plus, Minus, History } from "lucide-react";
import api, { formatPrice } from "@/lib/api";

const TIER_COLORS = {
  Bronze: { bg: "bg-orange-100", text: "text-orange-700", border: "border-orange-300" },
  Silver: { bg: "bg-gray-100", text: "text-gray-600", border: "border-gray-300" },
  Gold: { bg: "bg-yellow-50", text: "text-yellow-700", border: "border-yellow-400" },
  Platinum: { bg: "bg-green-50", text: "text-green-700", border: "border-green-400" },
};

export default function AdminLoyalty() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topClients, setTopClients] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editConfig, setEditConfig] = useState(null);
  const [adjustOpen, setAdjustOpen] = useState(null);
  const [adjustPoints, setAdjustPoints] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [historyOpen, setHistoryOpen] = useState(null);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/admin/loyalty/settings"),
      api.get("/admin/loyalty/top"),
    ]).then(([s, t]) => {
      setSettings(s.data);
      setTopClients(t.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = () => {
    setEditConfig(settings?.config ? JSON.parse(JSON.stringify(settings.config)) : {
      points_per_tg: 1, spend_unit: 100, tiers: []
    });
    setEditOpen(true);
  };

  const saveSettings = async () => {
    if (!editConfig) return;
    setSaving(true);
    try {
      await api.put("/admin/loyalty/settings", editConfig);
      setEditOpen(false);
      load();
    } catch (e) {
      console.error("Loyalty settings save failed:", e);
      alert(e.response?.data?.detail || "Не удалось сохранить настройки лояльности");
    }
    setSaving(false);
  };

  const openAdjust = (client) => {
    setAdjustOpen(client);
    setAdjustPoints("");
    setAdjustReason("");
  };

  const submitAdjust = async () => {
    if (!adjustOpen || !adjustPoints) return;
    setSaving(true);
    try {
      await api.post(`/admin/loyalty/points/${adjustOpen.id}`, {
        points: parseInt(adjustPoints),
        reason: adjustReason
      });
      setAdjustOpen(null);
      load();
    } catch (e) {
      console.error("Points adjust failed:", e);
      alert(e.response?.data?.detail || "Не удалось изменить баллы");
    }
    setSaving(false);
  };

  const openHistory = async (client) => {
    setHistoryOpen(client);
    try {
      const r = await api.get(`/admin/loyalty/history/${client.id}`);
      setHistory(r.data);
    } catch {
      setHistory([]);
    }
  };

  if (loading) return <div className="text-muted-foreground" data-testid="loyalty-loading">Загрузка...</div>;

  const config = settings?.config || {};
  const tiers = config.tiers || [];
  const tierStats = settings?.tier_stats || {};

  return (
    <div data-testid="admin-loyalty">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl flex items-center gap-3">
          <Award className="w-8 h-8 text-primary" />
          Программа лояльности
        </h1>
        <Button onClick={openEdit} className="bg-primary text-primary-foreground rounded-none" data-testid="edit-loyalty-settings-btn">
          Настройки
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="loyalty-stats">
        <div className="bg-card border border-border p-4 rounded-sm">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-primary" />
            <span className="text-xs text-muted-foreground">Всего клиентов</span>
          </div>
          <p className="text-2xl font-serif">{settings?.total_clients || 0}</p>
        </div>
        <div className="bg-card border border-border p-4 rounded-sm">
          <div className="flex items-center gap-2 mb-2">
            <Star className="w-4 h-4 text-yellow-500" />
            <span className="text-xs text-muted-foreground">Всего баллов</span>
          </div>
          <p className="text-2xl font-serif text-primary">{(settings?.total_points || 0).toLocaleString()}</p>
        </div>
        <div className="bg-card border border-border p-4 rounded-sm">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-green-500" />
            <span className="text-xs text-muted-foreground">Начисление</span>
          </div>
          <p className="text-lg font-serif">{config.points_per_tg || 1} балл / {formatPrice(config.spend_unit || 100)}</p>
        </div>
        <div className="bg-card border border-border p-4 rounded-sm">
          <div className="flex items-center gap-2 mb-2">
            <Crown className="w-4 h-4 text-yellow-600" />
            <span className="text-xs text-muted-foreground">Уровней</span>
          </div>
          <p className="text-2xl font-serif">{tiers.length}</p>
        </div>
      </div>

      {/* Tiers */}
      <div className="bg-card border border-border p-5 mb-6 rounded-sm">
        <h2 className="font-sans font-semibold text-sm uppercase tracking-widest mb-4 flex items-center gap-2">
          <Crown className="w-4 h-4 text-primary" /> Уровни лояльности
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="loyalty-tiers">
          {tiers.map((tier) => {
            const colors = TIER_COLORS[tier.name] || TIER_COLORS.Bronze;
            return (
              <div key={tier.name} className={`${colors.bg} border ${colors.border} p-4 rounded-sm`} data-testid={`tier-${tier.name.toLowerCase()}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`font-serif text-lg font-semibold ${colors.text}`}>{tier.name_ru || tier.name}</span>
                  <span className="w-4 h-4 rounded-full" style={{ backgroundColor: tier.color }} />
                </div>
                <div className="space-y-1 text-sm">
                  <p className="text-muted-foreground">От <span className="font-semibold text-foreground">{tier.min_points}</span> баллов</p>
                  <p className="text-muted-foreground">Скидка: <span className="font-semibold text-foreground">{tier.discount}%</span></p>
                  <p className="text-muted-foreground">Клиентов: <span className="font-semibold text-foreground">{tierStats[tier.name] || 0}</span></p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top Clients */}
      <div className="bg-card border border-border p-5 rounded-sm">
        <h2 className="font-sans font-semibold text-sm uppercase tracking-widest mb-4 flex items-center gap-2">
          <Award className="w-4 h-4 text-primary" /> Топ клиенты по баллам
        </h2>
        {topClients.length === 0 ? (
          <p className="text-muted-foreground text-sm py-4 text-center" data-testid="no-top-clients">Нет клиентов с баллами</p>
        ) : (
          <div className="space-y-2" data-testid="top-clients-list">
            {topClients.map((c, i) => {
              const tierName = c.loyalty_tier || "Bronze";
              const colors = TIER_COLORS[tierName] || TIER_COLORS.Bronze;
              return (
                <div key={c.id} className="flex items-center justify-between bg-background border border-border p-3 rounded-sm" data-testid={`top-client-${c.id}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-7 h-7 flex items-center justify-center bg-primary/10 text-primary font-serif text-sm rounded-full">
                      {i + 1}
                    </span>
                    <div>
                      <span className="font-sans font-medium">{c.name}</span>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-[10px] px-2 py-0.5 rounded-sm ${colors.bg} ${colors.text}`}>{tierName}</span>
                        <span className="text-xs text-muted-foreground">{c.phone}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-primary font-serif text-lg">{(c.loyalty_points || 0).toLocaleString()}</p>
                      <p className="text-[10px] text-muted-foreground">баллов</p>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => openAdjust(c)}
                        className="p-1.5 text-muted-foreground hover:text-primary transition-colors"
                        title="Начислить/списать баллы"
                        data-testid={`adjust-btn-${c.id}`}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openHistory(c)}
                        className="p-1.5 text-muted-foreground hover:text-primary transition-colors"
                        title="История баллов"
                        data-testid={`history-btn-${c.id}`}
                      >
                        <History className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Settings Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Настройки программы лояльности</DialogTitle>
          </DialogHeader>
          {editConfig && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Баллов за единицу</label>
                  <Input
                    type="number"
                    value={editConfig.points_per_tg}
                    onChange={(e) => setEditConfig({ ...editConfig, points_per_tg: parseInt(e.target.value) || 1 })}
                    className="bg-background border-border rounded-none"
                    data-testid="points-per-tg-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">За каждые (тг)</label>
                  <Input
                    type="number"
                    value={editConfig.spend_unit}
                    onChange={(e) => setEditConfig({ ...editConfig, spend_unit: parseInt(e.target.value) || 100 })}
                    className="bg-background border-border rounded-none"
                    data-testid="spend-unit-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-2 block">Уровни</label>
                <div className="space-y-2">
                  {editConfig.tiers?.map((tier, idx) => (
                    <div key={idx} className="grid grid-cols-4 gap-2 items-center">
                      <Input
                        value={tier.name_ru || tier.name}
                        onChange={(e) => {
                          const t = [...editConfig.tiers];
                          t[idx] = { ...t[idx], name_ru: e.target.value };
                          setEditConfig({ ...editConfig, tiers: t });
                        }}
                        className="bg-background border-border rounded-none text-sm"
                        placeholder="Название"
                      />
                      <Input
                        type="number"
                        value={tier.min_points}
                        onChange={(e) => {
                          const t = [...editConfig.tiers];
                          t[idx] = { ...t[idx], min_points: parseInt(e.target.value) || 0 };
                          setEditConfig({ ...editConfig, tiers: t });
                        }}
                        className="bg-background border-border rounded-none text-sm"
                        placeholder="Мин. баллов"
                      />
                      <Input
                        type="number"
                        value={tier.discount}
                        onChange={(e) => {
                          const t = [...editConfig.tiers];
                          t[idx] = { ...t[idx], discount: parseInt(e.target.value) || 0 };
                          setEditConfig({ ...editConfig, tiers: t });
                        }}
                        className="bg-background border-border rounded-none text-sm"
                        placeholder="Скидка %"
                      />
                      <Input
                        type="color"
                        value={tier.color || "#CD7F32"}
                        onChange={(e) => {
                          const t = [...editConfig.tiers];
                          t[idx] = { ...t[idx], color: e.target.value };
                          setEditConfig({ ...editConfig, tiers: t });
                        }}
                        className="h-9 w-full p-1 rounded-none cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
              <Button onClick={saveSettings} disabled={saving} className="bg-primary text-primary-foreground rounded-none w-full" data-testid="save-loyalty-settings-btn">
                {saving ? "Сохранение..." : "Сохранить"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Adjust Points Dialog */}
      <Dialog open={!!adjustOpen} onOpenChange={(o) => { if (!o) setAdjustOpen(null); }}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Баллы — {adjustOpen?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-muted-foreground">
              Текущий баланс: <span className="text-primary font-semibold">{(adjustOpen?.loyalty_points || 0).toLocaleString()}</span> баллов
            </p>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Баллы (+ начислить, - списать)</label>
              <Input
                type="number"
                value={adjustPoints}
                onChange={(e) => setAdjustPoints(e.target.value)}
                className="bg-background border-border rounded-none"
                placeholder="Например: 100 или -50"
                data-testid="adjust-points-input"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Причина</label>
              <Input
                value={adjustReason}
                onChange={(e) => setAdjustReason(e.target.value)}
                className="bg-background border-border rounded-none"
                placeholder="Бонус за День Рождения"
                data-testid="adjust-reason-input"
              />
            </div>
            <Button onClick={submitAdjust} disabled={saving || !adjustPoints} className="bg-primary text-primary-foreground rounded-none w-full" data-testid="submit-adjust-btn">
              {saving ? "..." : "Применить"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={!!historyOpen} onOpenChange={(o) => { if (!o) setHistoryOpen(null); }}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">История — {historyOpen?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Нет записей</p>
            ) : (
              history.map((h) => (
                <div key={h.id} className="flex items-center justify-between bg-background border border-border p-3 rounded-sm text-sm">
                  <div>
                    <p className="font-sans">{h.description}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {h.source === "admin" ? "Администратор" : h.source === "booking" ? "Бронирование" : "Заказ"}
                      {" · "}{new Date(h.created_at).toLocaleDateString("ru-RU")}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`font-serif text-lg ${h.points > 0 ? "text-green-600" : "text-red-500"}`}>
                      {h.points > 0 ? "+" : ""}{h.points}
                    </span>
                    <p className="text-[10px] text-muted-foreground">Баланс: {h.balance_after}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
